import React from "react";
import { useQuery } from "@tanstack/react-query";
import { api, errMsg } from "@/lib/api";
import { useT } from "@/context/I18nContext";
import PageHeader from "@/components/common/PageHeader";
import { LoadingCard, ErrorState } from "@/components/common/EmptyState";
import { Users, MessageSquare, ShieldAlert, Flag, Activity, Star } from "lucide-react";

function Stat({ icon: Icon, label, value, testId }) {
  return (
    <div className="rounded-3xl bg-white shadow-card p-6" data-testid={testId}>
      <div className="flex items-center gap-2 text-ink-500 text-[11px] font-semibold uppercase tracking-[0.15em]">
        <Icon className="h-3.5 w-3.5" strokeWidth={1.75} /> {label}
      </div>
      <div className="font-heading text-3xl text-ink-900 mt-2 tracking-tight">{value ?? "—"}</div>
    </div>
  );
}

export default function AdminHome() {
  const t = useT();
  const stats = useQuery({
    queryKey: ["admin-stats"],
    queryFn: async () => (await api.get("/admin/stats")).data,
    retry: false,
  });
  const usage = useQuery({
    queryKey: ["admin-usage-summary"],
    queryFn: async () => (await api.get("/admin/usage/summary")).data,
    retry: false,
  });

  const s = stats.data || {};
  const u = usage.data || {};

  return (
    <div className="space-y-8">
      <PageHeader eyebrow="Admin" title={t("admin.title")} subtitle={t("admin.subtitle")} />
      {stats.isLoading && <LoadingCard height="h-24" />}
      {stats.isError && <ErrorState message={errMsg(stats.error)} onRetry={() => stats.refetch()} />}
      {!stats.isLoading && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          <Stat testId="admin-stat-users" icon={Users} label={t("admin.activeUsers")} value={s.users ?? s.active_users ?? u.active_users} />
          <Stat testId="admin-stat-conversations" icon={MessageSquare} label={t("admin.conversations")} value={s.conversations ?? u.conversations} />
          <Stat testId="admin-stat-crises" icon={ShieldAlert} label={t("admin.crises")} value={s.crises ?? s.crisis_alerts ?? u.crises} />
          <Stat testId="admin-stat-flagged" icon={Flag} label={t("admin.flagged")} value={s.flagged ?? u.flagged} />
          <Stat testId="admin-stat-pending" icon={Users} label={t("admin.pendingApprovals")} value={s.pending ?? s.pending_approvals ?? u.pending} />
          <Stat testId="admin-stat-events" icon={Activity} label={t("nav.usage")} value={u.events ?? u.total_events} />
        </div>
      )}
      {usage.data && (
        <div className="rounded-3xl bg-white shadow-card p-6">
          <div className="flex items-center gap-2 text-ink-500 text-[11px] font-semibold uppercase tracking-[0.15em]">
            <Star className="h-3.5 w-3.5" /> {t("nav.usage")} · summary
          </div>
          <pre className="mt-3 text-xs text-ink-700 whitespace-pre-wrap font-mono max-h-72 overflow-auto">
            {JSON.stringify(usage.data, null, 2)}
          </pre>
        </div>
      )}
    </div>
  );
}
